/**
 * @author: lv.chen
 * @date: 2026/03/25
 * @desc: CasualGamesHub/CasualTopChartsMahjong - Mahjong Top Charts 页面
 */
import React, { useMemo } from 'react';
import classnames from 'classnames';
import { history } from 'umi';
import ReuseTabPageTemplate from '@intelligence/v3/components/ReuseTabPageTemplate';
import ActiveUsers from '@intelligence/v3/pages/TopCharts/MobileGames/ActiveUsers';
import useUrlSyncActiveId from '@intelligence/v3/_hooks/useUrlSyncActiveId';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';

type TTabId = 'mahjong_am' | 'mahjong_st';

interface ITabConfig {
  id: TTabId;
  label: string;
  annotation: string;
  pageLogKey: string;
  downloadLogKey: string;
  condKey?: string;
}

interface ICasualTopChartsMahjongProps extends IBaseProps {}

const TAB_IDS: TTabId[] = ['mahjong_st', 'mahjong_am'];
const DEFAULT_ACTIVE_TAB_ID: TTabId = 'mahjong_am';
const PAGE_PATH_PREFIX = '/intelligence/casualGamesHub/casualTopChartsMahjong';

const CasualTopChartsMahjong: React.FC<ICasualTopChartsMahjongProps> = (props) => {
  const { style, className } = props;

  const { formatMessage } = useIntlExtend();
  const query = history.location.query || {};
  const [activeId, setActiveId] = useUrlSyncActiveId(TAB_IDS, DEFAULT_ACTIVE_TAB_ID);

  const tabCondKeysFromBackend = useMemo(() => {
    try {
      const encoded = query.tab_cond_keys as string;
      if (!encoded) {
        return {};
      }
      return JSON.parse(decodeURIComponent(encoded));
    } catch (error) {
      console.error('[Tab Cond Keys] Parse error:', error);
      return {};
    }
  }, [query.tab_cond_keys]);

  const tabConfigs = useMemo(() => {
    const configs: Record<TTabId, ITabConfig> = {
      mahjong_am: {
        id: 'mahjong_am',
        label: formatMessage({ id: 'dbi_tab_label_mahjong_am' }),
        annotation: formatMessage({ id: 'dbi_tab_annotation_mahjong_am' }),
        pageLogKey: 'casualGamesHubMahjongTopChartsAppMagic',
        downloadLogKey: 'casualGamesHubMahjongTopChartsAppMagicDownloadClick',
      },
      mahjong_st: {
        id: 'mahjong_st',
        label: formatMessage({ id: 'dbi_tab_label_mahjong_st' }),
        annotation: formatMessage({ id: 'dbi_tab_annotation_mahjong_st' }),
        pageLogKey: 'casualGamesHubMahjongTopChartsSensortower',
        downloadLogKey: 'casualGamesHubMahjongTopChartsSensortowerDownloadClick',
      },
    };

    Object.keys(configs).forEach((tabId) => {
      const backendCondKey = (tabCondKeysFromBackend as Record<string, string>)[tabId];
      if (backendCondKey) {
        configs[tabId as keyof typeof configs].condKey = backendCondKey;
      }
    });

    return configs;
  }, [formatMessage, tabCondKeysFromBackend]);

  /**
   * Handle tab change and update URL with filter_tag_name for all tabs
   * This triggers the cond API to fetch filter configuration
   */
  const handleTabChange = (tab: { id: string }): void => {
    const tabId = (tab.id as TTabId) || DEFAULT_ACTIVE_TAB_ID;
    if (tabId === activeId) {
      return;
    }

    setActiveId(tabId);

    const currentQuery = history.location.query || {};
    const condKey = tabConfigs[tabId]?.condKey;

    history.push({
      pathname: `${PAGE_PATH_PREFIX}/${tabId}`,
      query: {
        ...currentQuery,
        ...(condKey ? { filter_tag_name: condKey } : {}),
      },
    });
  };

  const tabs = useMemo(() => {
    return TAB_IDS.map((tabId) => {
      const tabConfig = tabConfigs[tabId];
      return {
        id: tabConfig.id,
        label: tabConfig.label,
        component: (
          <ActiveUsers
            pageLogConfigKey={tabConfig.pageLogKey}
            downloadLogConfigKey={tabConfig.downloadLogKey}
            disableInheritance={true}
          />
        ),
      };
    });
  }, [tabConfigs]);

  const activeTab = tabs.find((item) => item.id === activeId) || tabs[0];
  const activeConfig = tabConfigs[activeId as TTabId];

  return (
    <ReuseTabPageTemplate
      style={style}
      className={classnames('dbi-reuse-page-mahjong_am-top-charts-am', className)}
      title={formatMessage({ id: 'dbi_reuse_page_title_top_charts_mahjong' })}
      subTitle={activeConfig?.annotation}
      pageLogKeyMap={{
        mahjong_am: tabConfigs.mahjong_am.pageLogKey,
        mahjong_st: tabConfigs.mahjong_st.pageLogKey,
      }}
      tabs={tabs}
      activeId={activeId}
      onActive={handleTabChange}
    >
      <div key={activeId}>{activeTab?.component}</div>
    </ReuseTabPageTemplate>
  );
};

export default CasualTopChartsMahjong;
